import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Button,
  Select,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Badge,
  Flex,
  VStack,
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../lib/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  setDoc, 
  query, 
  where, 
  writeBatch,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';

interface Match {
  id: string;
  phase: string;
  team_a: string;
  team_b: string;
  score_a: number | null;
  score_b: number | null;
  activo: boolean;
  date: Timestamp;
  created_at: Timestamp;
  updated_at: Timestamp;
}

interface GroupedMatches {
  [phase: string]: Match[];
}

const teams = [
  'América',
  'Guadalajara',
  'Cruz Azul',
  'Pumas UNAM',
  'Monterrey',
  'Tigres UANL',
  'Santos Laguna',
  'Atlas',
  'Toluca',
  'León',
  'Pachuca',
  'Tijuana',
  'Necaxa',
  'Puebla',
  'Querétaro',
  'Mazatlán FC',
  'FC Juárez',
  'Atlético San Luis'
];

const phases = [
  'Jornada 1', 'Jornada 2', 'Jornada 3', 'Jornada 4', 
                'Jornada 5', 'Jornada 6', 'Jornada 7', 'Jornada 8', 
                'Jornada 9', 'Jornada 10', 'Jornada 11', 'Jornada 12', 
                'Jornada 13', 'Jornada 14', 'Jornada 15', 'Jornada 16', 
  'Jornada 17',
  'Cuartos Ida', 'Cuartos Vuelta',
  'Semifinal Ida', 'Semifinal Vuelta',
  'Final Ida', 'Final Vuelta'
];

const ManageMatches: React.FC = () => {
  const [selectedPhase, setSelectedPhase] = useState('');
  const [teamA, setTeamA] = useState('');
  const [teamB, setTeamB] = useState('');
  const [matches, setMatches] = useState<GroupedMatches>({});
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { user } = useAuth();

  useEffect(() => {
    loadMatches();
  }, []);

  const loadMatches = async () => {
    try {
      const matchesRef = collection(db, 'matches');
      const matchesSnap = await getDocs(matchesRef);
      const matchesData = matchesSnap.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({
        id: doc.id,
        ...doc.data()
      })) as Match[];

      // Agrupar por fase
      const groupedMatches = matchesData.reduce((acc: GroupedMatches, match) => {
        if (!acc[match.phase]) {
          acc[match.phase] = [];
        }
        acc[match.phase].push(match);
        return acc;
      }, {});

      setMatches(groupedMatches);
    } catch (error) {
      console.error('Error al cargar partidos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los partidos',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPhase || !teamA || !teamB) {
      toast({
        title: 'Error',
        description: 'Por favor completa todos los campos',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      const matchRef = doc(collection(db, 'matches'));
      await setDoc(matchRef, {
        phase: selectedPhase,
        team_a: teamA,
        team_b: teamB,
        score_a: null,
        score_b: null,
        activo: true,
        date: Timestamp.now(),
        created_at: Timestamp.now(),
        updated_at: Timestamp.now()
      });

      toast({
        title: 'Éxito',
        description: 'Partido creado correctamente',
        status: 'success',
        duration: 3000,
      });

      // Limpiar formulario
      setSelectedPhase('');
      setTeamA('');
      setTeamB('');
      
      // Recargar partidos
      loadMatches();
    } catch (error) {
      console.error('Error al crear partido:', error);
      toast({
        title: 'Error',
        description: 'No se pudo crear el partido',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePhase = async (phase: string, active: boolean) => {
    try {
      const matchesRef = collection(db, 'matches');
      const q = query(matchesRef, where('phase', '==', phase));
      const querySnapshot = await getDocs(q);

      const batch = writeBatch(db);
      querySnapshot.docs.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        batch.update(doc.ref, { activo: active });
      });

      await batch.commit();
      toast({
        title: 'Éxito',
        description: `Jornada ${active ? 'activada' : 'desactivada'} correctamente`,
        status: 'success',
        duration: 3000,
      });

      loadMatches();
    } catch (error) {
      console.error('Error al actualizar fase:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la jornada',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleResetMatch = async (matchId: string) => {
    try {
      // 1. Reinicia el resultado real del partido
      const matchRef = doc(db, 'matches', matchId);
      await updateDoc(matchRef, {
        score_a: null,
        score_b: null
      });

      // 2. Elimina los pronósticos de ese partido
      const predictionsRef = collection(db, 'predictions');
      const q = query(predictionsRef, where('match_id', '==', matchId));
      const predictionsSnap = await getDocs(q);
      const batchDeletes = predictionsSnap.docs.map(docSnap => deleteDoc(docSnap.ref));
      await Promise.all(batchDeletes);

      toast({
        title: 'Partido reiniciado',
        description: 'El resultado y los pronósticos han sido reiniciados.',
        status: 'success',
        duration: 3000,
      });

      // (Opcional) Recarga los partidos
      // await loadMatches();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo reiniciar el partido.',
        status: 'error',
        duration: 3000,
      });
    }
  };

  if (!user || user.email !== 'htorres@teiker.mx') {
    return (
      <Box p={4}>
        <Heading size="md">No tienes acceso a esta página</Heading>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch" p={4}>
      <Heading>Administración de Partidos</Heading>
      
      {/* Formulario para agregar partidos */}
      <Card>
        <CardHeader>
          <Heading size="md">Agregar Nuevo Partido</Heading>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Fase</FormLabel>
                <Select 
                  value={selectedPhase} 
                  onChange={(e) => setSelectedPhase(e.target.value)}
                  placeholder="Selecciona la fase"
                >
                  {phases.map((phase) => (
                    <option key={phase} value={phase}>{phase}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Equipo Local</FormLabel>
                <Select 
                  value={teamA} 
                  onChange={(e) => setTeamA(e.target.value)}
                  placeholder="Selecciona el equipo local"
                >
                  {teams.map((team) => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Equipo Visitante</FormLabel>
                <Select 
                  value={teamB} 
                  onChange={(e) => setTeamB(e.target.value)}
                  placeholder="Selecciona el equipo visitante"
                >
                  {teams.map((team) => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </Select>
              </FormControl>

              <Button 
                type="submit" 
                colorScheme="blue" 
                isLoading={loading}
                width="full"
              >
                Agregar Partido
              </Button>
            </VStack>
          </form>
        </CardBody>
      </Card>

      {/* Lista de partidos por fase */}
      {Object.entries(matches).map(([phase, phaseMatches]) => (
        <Card key={phase}>
          <CardHeader>
            <Flex justify="space-between" align="center">
              <Heading size="md">{phase}</Heading>
              <Button
                size="sm"
                colorScheme={phaseMatches[0]?.activo ? "red" : "green"}
                onClick={() => togglePhase(phase, !phaseMatches[0]?.activo)}
              >
                {phaseMatches[0]?.activo ? "Desactivar" : "Activar"} Jornada
              </Button>
            </Flex>
          </CardHeader>
          <CardBody>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Local</Th>
            <Th>Visitante</Th>
            <Th>Estado</Th>
            <Th>Acciones</Th>
          </Tr>
        </Thead>
        <Tbody>
                {phaseMatches.map((match) => (
                  <Tr key={match.id}>
                    <Td>{match.team_a}</Td>
                    <Td>{match.team_b}</Td>
                    <Td>
                      <Badge colorScheme={match.activo ? "green" : "red"}>
                        {match.activo ? "Activo" : "Inactivo"}
                      </Badge>
                    </Td>
                    <Td>
                      <Button
                        colorScheme="red"
                        size="sm"
                        onClick={() => handleResetMatch(match.id)}
                      >
                        Reiniciar
                      </Button>
                    </Td>
                  </Tr>
                ))}
        </Tbody>
      </Table>
          </CardBody>
        </Card>
      ))}
    </VStack>
  );
};

export default ManageMatches; 